export const QC_OPTIONS = [
  'Quantity A is greater',
  'Quantity B is greater',
  'The two quantities are equal',
  'The relationship cannot be determined',
];

export const getGreQuantQuestionType = (question) => {
  if (question?.questionType) return question.questionType;
  if (question?.acceptedAnswers || question?.correctAnswer !== undefined)
    return 'numeric_entry';
  if (Array.isArray(question?.correct)) return 'multiple_choice_multi';
  return 'multiple_choice_single';
};

export const normalizeNumericEntry = (value) => {
  let s = String(value ?? '').trim();
  // Accept "x=4" / "x = 4" the same as "4" for numeric entry.
  const assignment = s.match(/^([a-zA-Z])\s*=\s*(.+)$/);
  if (assignment) {
    s = assignment[2].trim();
  }
  s = s.replace(/\s+/g, '').replace(/,/g, '');
  if (s.startsWith('+') && s.length > 1 && /\d/.test(s[1])) {
    s = s.slice(1);
  }
  return s;
};

export const tryParseRational = (raw) => {
  const value = normalizeNumericEntry(raw);
  if (!value) return null;
  if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);

  const fraction = value.match(/^(-?\d+)\s*\/\s*(-?\d+)$/);
  if (!fraction) return null;

  const numerator = Number(fraction[1]);
  const denominator = Number(fraction[2]);
  if (denominator === 0) return null;

  return numerator / denominator;
};

export const isGreQuantAnswerCorrect = (question, userAnswer) => {
  const type = getGreQuantQuestionType(question);

  if (type === 'numeric_entry') {
    const acceptedAnswers = Array.isArray(question.acceptedAnswers)
      ? question.acceptedAnswers
      : question.correctAnswer !== undefined
        ? [question.correctAnswer]
        : [];
    const userValue = tryParseRational(userAnswer);
    if (userValue === null) return false;

    return acceptedAnswers.some((answer) => {
      const correctValue = tryParseRational(answer);
      return correctValue !== null && Math.abs(userValue - correctValue) < 1e-9;
    });
  }

  if (type === 'multiple_choice_multi') {
    if (!Array.isArray(userAnswer) || !Array.isArray(question.correct)) {
      return false;
    }

    const selected = [...new Set(userAnswer)].sort((a, b) => a - b);
    const correct = [...new Set(question.correct)].sort((a, b) => a - b);
    return (
      selected.length === correct.length &&
      selected.every((value, index) => value === correct[index])
    );
  }

  return userAnswer === question.correct;
};

export const isGreQuantQuestionAnswered = (question, userAnswer) => {
  const type = getGreQuantQuestionType(question);

  if (type === 'numeric_entry') {
    return normalizeNumericEntry(userAnswer).length > 0;
  }

  if (type === 'multiple_choice_multi') {
    return Array.isArray(userAnswer) && userAnswer.length > 0;
  }

  return userAnswer !== undefined;
};

export const getGreQuantCorrectAnswerText = (question) => {
  const type = getGreQuantQuestionType(question);

  if (type === 'numeric_entry') {
    const answers = Array.isArray(question.acceptedAnswers)
      ? question.acceptedAnswers
      : question.correctAnswer !== undefined
        ? [question.correctAnswer]
        : [];
    return answers.join(' or ');
  }

  const options =
    type === 'quantitative_comparison' ? QC_OPTIONS : question.options || [];

  if (type === 'multiple_choice_multi') {
    return Array.isArray(question.correct)
      ? question.correct.map((index) => options[index]).join('; ')
      : '';
  }

  return options[question.correct] || '';
};

export const getGreQuantUserAnswerText = (question, userAnswer) => {
  const type = getGreQuantQuestionType(question);

  if (type === 'numeric_entry')
    return normalizeNumericEntry(userAnswer) || 'No answer';

  const options =
    type === 'quantitative_comparison' ? QC_OPTIONS : question.options || [];

  if (type === 'multiple_choice_multi') {
    return Array.isArray(userAnswer) && userAnswer.length
      ? userAnswer.map((index) => options[index]).join('; ')
      : 'No answer';
  }

  return options[userAnswer] || 'No answer';
};
